import { createApi, fetchBaseQuery, type BaseQueryFn, type FetchArgs, type FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { endSession, isAuthPath, refreshAccessToken } from '@/lib/session';

// ─── Types (shared with api.ts) ───────────────────────────────────────────────

export interface StaffMember {
  id: string;
  fullName: string;
  email: string;
  role: 'gate_officer' | 'teacher' | 'institution_admin';
  isActive: boolean;
  createdAt: string;
  /* A teacher's classes, so the edit form opens with the right boxes ticked. */
  groupIds: string[];
  photoUrl: string | null;
  /* A report card is signed by the person who wrote the remark, so whether
     they have uploaded a signature is worth seeing before term ends rather
     than discovering on the day the cards are printed. */
  hasSignature: boolean;
  signatureUrl: string | null;
}

export interface CreateStaffData {
  fullName: string;
  email: string;
  password: string;
  role: 'gate_officer' | 'teacher' | 'institution_admin';
  /** Teachers only - the classes they take. */
  groupIds?: string[];
}

export interface Group {
  _id: string;
  name: string;
  description?: string;
  order: number;
  isActive: boolean;
}

export interface StudentSummary {
  _id: string;
  studentCode: string;
  fullName: string;
  class: string;       // = group.name (backward-compat alias from API)
  groupId: string | null;
  status: string;
  dateOfBirth: string;
  primaryCollectorPhone: string;
  photoS3Key?: string;
}

/* ── Attendance ─────────────────────────────────────────────────────────────
   `unmarked` is not a kind of present. It means no row exists — nobody has
   decided — and the register has to show it, because "the register was never
   taken in Class 3" is what a school needs to know before the gate opens. */
export type AttendanceState = 'present' | 'absent' | 'unmarked';

export interface RegisterRow {
  studentId: string;
  fullName: string;
  studentClass: string;
  groupId: string | null;
  status: AttendanceState;
  markedAt: string | null;
  note: string;
}

/* Named for the REPORT, not the board — `DismissalRow` is already the live
   board's row and the two are different shapes for different questions. */
export interface DismissalRegisterRow {
  studentId: string;
  fullName: string;
  studentClass: string;
  attendance: 'present' | 'absent' | 'unmarked';
  calledAt: string | null;
  calledVia: string | null;
  releasedAt: string | null;
  releasedBy: string;
  deviceId: string;
  offline: boolean;
  primaryCollector: string;
  outcome: 'released' | 'not_released' | 'absent';
  /** Released although the register said absent — only reachable offline. */
  flagged: boolean;
}

export interface DismissalRegister {
  date: string;
  onRoll: number;
  present: number;
  absent: number;
  unmarked: number;
  released: number;
  notReleased: number;
  rows: DismissalRegisterRow[];
}

/* ── Timetable ──────────────────────────────────────────────────────────────
   Break and lunch are periods with `isTeaching: false`. They are rows on the
   grid, not gaps, or it stops matching the day a child experiences. */
export interface Period {
  _id: string;
  name: string;
  startTime: string;
  endTime: string;
  order: number;
  isTeaching: boolean;
}

export interface TimetableSlot {
  _id: string;
  day: number;
  periodId: string;
  subjectId: string;
  subject: string;
  subjectCode: string;
  teacherId: string | null;
  teacher: string;
  room: string;
}

/*
  What the grid cannot be read for.

  Nobody can look at 35 boxes and say whether four are empty or fourteen, or
  whether Class 3 is getting its five Maths periods. The server counts it.
*/
export interface GridSummary {
  teachingSlots: number;
  filled: number;
  empty: number;
  /* A lesson with nobody down to teach it — fine in August, a problem in
     September, and invisible unless it is counted. */
  unstaffed: number;
  bySubject: Array<{ subjectId: string; subject: string; periods: number }>;
}

export interface ClassTimetable {
  periods: Period[];
  slots: TimetableSlot[];
  summary: GridSummary;
}

export interface TeacherLoadRow {
  teacherId: string;
  fullName: string;
  periods: number;
  classes: number;
}

export interface TeacherTimetable {
  periods: Period[];
  slots: Array<TimetableSlot & { groupId: string; className: string }>;
}

export interface ChildTimetable {
  periods: Period[];
  slots: TimetableSlot[];
  studentId: string;
  studentName: string;
  className: string;
}

export interface AttendanceRegister {
  date: string;
  onRegister: number;
  present: number;
  absent: number;
  unmarked: number;
  /** True only when every child has a decision. What "register taken" means. */
  complete: boolean;
  rows: RegisterRow[];
}

/* ── Exam results ───────────────────────────────────────────────────────────
   Both scores are out of 100 and the exam's own weights combine them, because
   schools write continuous assessment down out of 30, 40 or anything else and
   a sheet whose columns have different denominators gets read wrong. */
export interface Subject {
  _id: string;
  name: string;
  code: string;
  groupIds: string[];
  order: number;
  isActive: boolean;
  /* Why a delete may be refused. Carried so the screen can SAY so rather than
     grey a button out and leave somebody guessing. */
  marksRecorded: number;
}

export interface GradeBand {
  minScore: number;
  grade: string;
  remark: string;
}

/* 'weight' — class /30 + exam /70, added, which is what a mark sheet holds.
   'percent' — both /100, combined by the weights. */
export type MarkScale = 'weight' | 'percent';

/* A term holds more than one assessment — a mid-term alongside the end-of-term
   paper, or a mock before the BECE. This is what the printed report calls
   itself, so a mid-term does not go home headed "Terminal Report". */
export type ExamKind = 'mid_term' | 'end_of_term' | 'mock' | 'other';

export const EXAM_KIND_LABEL: Record<ExamKind, string> = {
  mid_term: 'Mid-term',
  end_of_term: 'End of term',
  mock: 'Mock',
  other: 'Other',
};

export interface ExamSummary {
  _id: string;
  name: string;
  academicYear: string;
  term: string;
  kind: ExamKind;
  groupIds: string[];
  classScoreWeight: number;
  examScoreWeight: number;
  markScale: MarkScale;
  /* This exam's own copy of the scale. Every grade on its report cards was
     decided by these, whatever the school uses now. */
  gradeBands: GradeBand[];
  status: 'draft' | 'published';
  publishedAt: string | null;
  marksEntered: number;
}

export interface SheetRow {
  studentId: string;
  fullName: string;
  classScore: number | null;
  examScore: number | null;
  total: number | null;
  grade: string;
  unmarked: boolean;
}

export interface MarkSheet {
  rows: SheetRow[];
  entered: number;
  onSheet: number;
  classScoreWeight: number;
  examScoreWeight: number;
  markScale: MarkScale;
  /* The ceiling on each column. The screen must not print "/100" over a column
     the API will refuse above 30. */
  classMax: number;
  examMax: number;
  status: 'draft' | 'published';
}

export interface SubjectLine {
  subjectId: string;
  subject: string;
  code: string;
  classScore: number;
  examScore: number;
  total: number;
  grade: string;
  remark: string;
  position: number;
  classSize: number;
}

export interface ReportCard {
  examId: string;
  examName: string;
  academicYear: string;
  term: string;
  kind: ExamKind;
  /** What the printed report calls itself. */
  heading: string;
  /* How the two marks were combined — on the card, because a footnote
     explaining a total has to travel with the total. */
  markScale: MarkScale;
  classScoreWeight: number;
  examScoreWeight: number;
  status: string;
  studentId: string;
  studentName: string;
  studentClass: string;
  subjects: SubjectLine[];
  subjectsTaken: number;
  totalScore: number;
  average: number;
  overallGrade: string;
  overallRemark: string;
  position: number;
  classSize: number;
  classTeacherRemark: string;
  headTeacherRemark: string;
  /* Whoever WROTE each remark, and their signature — not whoever holds the
     role today, so a card reprinted in two years still shows the teacher who
     actually taught the child that term. */
  classTeacher: { fullName: string; signatureUrl: string | null } | null;
  headTeacher: { fullName: string; signatureUrl: string | null } | null;
  /* Carried ON the card so it is self-contained — a parent cannot reach the
     admin endpoint the school-side page used to read this from. */
  school: { name: string; address: string; phone: string; logoUrl: string | null };
  /* The scale THIS card was graded on — printed as a key, and the plainest
     demonstration that an old card keeps its old boundaries. */
  gradeBands: GradeBand[];
}

export interface ChildResults {
  studentId: string;
  studentName: string;
  reports: ReportCard[];
}

export interface CreateStudentData {
  fullName: string;
  dateOfBirth: string;
  groupId: string | null;
  primaryCollectorPhone: string;
  status?: string;
}

export interface CollectorSummary {
  _id: string;
  fullName: string;
  relationship: string;
  phoneE164: string;
  additionalPhones: string[];
  idType: string;
  isPrimary: boolean;
  isActive: boolean;
}

/*
  What the API actually returns for every delegate endpoint.

  This type used to declare `_id`, `fullName`, `phoneE164` and `type`. None of
  those exist on the wire: delegates.controller.ts maps every delegate through
  `toParentView()` before sending, which renames the fields and collapses `type`
  to the boolean `isRecurring`. So the dashboard was reading four fields that
  were always undefined — silently, because the type said they were there.

  Note `relationship` is accepted when CREATING a delegate but is not echoed
  back, so it cannot be displayed in a list.
*/
export interface DelegateSummary {
  id: string;
  name: string;
  phone: string;
  isRecurring: boolean;
  validDate?: string;
  isActive: boolean;
  createdAt: string;
}

export interface AddDelegateData {
  fullName: string;
  phoneE164: string;
  relationship: string;
  type: 'recurring' | 'one_day' | 'scheduled';
  validDate?: string;
}

export interface AuditEvent {
  _id: string;
  studentId: string;
  institutionId: string;
  eventType: string;
  gateOfficerId?: string;
  deviceId?: string;
  isOfflineEvent: boolean;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface PickupBoard {
  institutionId: string;
  date: string;
  total: number;
  released: number;
  pending: number;
  flagged: number;
}

export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/* ── Fees ─────────────────────────────────────────────────────────────────────
   Every amount crossing this boundary is an integer of pesewas. The only place
   a decimal exists is `formatMinor` in FeesPage — see the note there. */

export type Term = 'term_1' | 'term_2' | 'term_3';
export type InvoiceStatus = 'unpaid' | 'part_paid' | 'paid' | 'cancelled';
export type PaymentMethod = 'cash' | 'momo' | 'bank_transfer' | 'cheque' | 'waiver';

export interface FeeItem {
  label: string;
  amountMinor: number;
}

export interface FeeStructure {
  _id: string;
  name: string;
  academicYear: string;
  term: Term;
  groupId: string | null;
  items: FeeItem[];
  totalMinor: number;
  currency: string;
  dueDate: string;
  isActive: boolean;
}

export interface InvoiceRow {
  _id: string;
  invoiceNumber: string;
  studentId: string;
  studentName: string;
  studentCode: string;
  groupName: string;
  academicYear: string;
  term: Term;
  totalMinor: number;
  paidMinor: number;
  balanceMinor: number;
  currency: string;
  dueDate: string;
  status: InvoiceStatus;
  isOverdue: boolean;
}

export interface InvoicePayment {
  _id: string;
  receiptNumber: string;
  amountMinor: number;
  method: PaymentMethod;
  reference: string;
  note: string;
  receivedAt: string;
}

export interface InvoiceDetail extends Omit<InvoiceRow, 'studentCode'> {
  studentCode: string;
  parentPhone: string;
  items: FeeItem[];
  payments: InvoicePayment[];
}

export interface FeeSummary {
  billedMinor: number;
  collectedMinor: number;
  outstandingMinor: number;
  invoiceCount: number;
  paidCount: number;
  overdueCount: number;
  currency: string;
}

export interface RaiseResult {
  raised: number;
  skipped: number;
  totalBilledMinor: number;
}

/* What a parent sees — their own children only, and never a write. */

export interface ParentInvoiceRow {
  _id: string;
  invoiceNumber: string;
  academicYear: string;
  term: Term;
  totalMinor: number;
  paidMinor: number;
  balanceMinor: number;
  currency: string;
  dueDate: string;
  status: InvoiceStatus;
  isOverdue: boolean;
}

export interface ChildFees {
  studentId: string;
  studentName: string;
  studentClass: string;
  billedMinor: number;
  paidMinor: number;
  balanceMinor: number;
  invoices: ParentInvoiceRow[];
}

export interface ParentFees {
  currency: string;
  billedMinor: number;
  paidMinor: number;
  balanceMinor: number;
  overdueCount: number;
  children: ChildFees[];
}

export interface ParentInvoiceDetail {
  _id: string;
  invoiceNumber: string;
  studentName: string;
  studentClass: string;
  academicYear: string;
  term: Term;
  items: FeeItem[];
  totalMinor: number;
  paidMinor: number;
  balanceMinor: number;
  currency: string;
  dueDate: string;
  status: InvoiceStatus;
  isOverdue: boolean;
  payments: Array<{
    _id: string;
    receiptNumber: string;
    amountMinor: number;
    method: PaymentMethod;
    reference: string;
    receivedAt: string;
  }>;
}

/* ── Events ───────────────────────────────────────────────────────────────── */

export type EventCategory = 'sports' | 'academic' | 'meeting' | 'holiday' | 'exam' | 'other';
export type EventStatus = 'draft' | 'published' | 'cancelled';
export type RsvpAnswer = 'going' | 'not_going';

export interface SchoolEvent {
  _id: string;
  title: string;
  description: string;
  category: EventCategory;
  startAt: string;
  endAt: string;
  allDay: boolean;
  location: string;
  groupIds: string[];
  groupNames: string[];
  rsvpRequired: boolean;
  rsvpDeadline: string | null;
  reminderOffsetsHours: number[];
  status: EventStatus;
  publishedAt: string | null;
  cancellationReason: string;
  counts: { going: number; notGoing: number; guests: number; audience: number };
}

/* What the hourly job WOULD do, computed without sending anything. */
export interface ReminderPreview {
  eventId: string;
  title: string;
  status: EventStatus;
  startAt: string;
  /** The three gates, reported separately so it is obvious which one is shut. */
  sendingEnabled: boolean;
  isPublished: boolean;
  offsets: number[];
  recipientCount: number;
  childCount: number;
  schedule: Array<{
    offsetHours: number;
    triggerAt: string;
    whenText: string;
    sampleMessage: string;
    sentCount: number;
    failedCount: number;
    state: 'scheduled' | 'due' | 'done' | 'missed';
  }>;
  recipients: Array<{ phone: string; childCount: number }>;
}

export interface EventResponses {
  eventId: string;
  title: string;
  audience: number;
  going: number;
  notGoing: number;
  guests: number;
  awaiting: number;
  responses: Array<{
    _id: string;
    studentId: string;
    studentName: string;
    groupName: string;
    response: RsvpAnswer;
    guestCount: number;
    note: string;
    respondedAt: string;
  }>;
}

export interface EventInput {
  title: string;
  description?: string;
  category: EventCategory;
  startAt: string;
  endAt: string;
  allDay: boolean;
  location?: string;
  groupIds: string[];
  rsvpRequired: boolean;
  rsvpDeadline?: string;
  reminderOffsetsHours: number[];
}

/* `ParentEvent` is already taken by the pickup-history item, so the calendar
   one carries the longer name rather than shadowing it. */
export interface ParentCalendarEvent {
  _id: string;
  title: string;
  description: string;
  category: EventCategory;
  startAt: string;
  endAt: string;
  allDay: boolean;
  location: string;
  status: EventStatus;
  cancellationReason: string;
  rsvpRequired: boolean;
  rsvpDeadline: string | null;
  rsvpClosed: boolean;
  children: Array<{
    studentId: string;
    studentName: string;
    response: RsvpAnswer | null;
    guestCount: number;
    note: string;
  }>;
}

/* ── Notices ──────────────────────────────────────────────────────────────── */

export type NoticeStatus = 'draft' | 'scheduled' | 'published' | 'cancelled';
export type NoticePriority = 'normal' | 'urgent';

export interface Notice {
  _id: string;
  title: string;
  body: string;
  priority: NoticePriority;
  groupIds: string[];
  groupNames: string[];
  sendPush: boolean;
  sendSms: boolean;
  status: NoticeStatus;
  scheduledFor: string | null;
  publishedAt: string | null;
  createdAt: string;
  delivery: { smsSent: number; smsFailed: number; pushSent: number; skipped: number };
}

export interface NoticeInput {
  title: string;
  body: string;
  priority: NoticePriority;
  groupIds: string[];
  sendPush: boolean;
  sendSms: boolean;
  scheduledFor?: string | null;
}

/* Every channel reported with WHY it will or will not reach anyone — a bare
   count makes a disabled gate look identical to an empty audience. */
export interface NoticePreview {
  noticeId: string;
  title: string;
  status: NoticeStatus;
  recipientCount: number;
  childCount: number;
  inApp: { reach: number };
  push: { enabled: boolean; reachable: number; unreachable: number; configured: boolean };
  sms: { enabled: boolean; serverEnabled: boolean; reachable: number; cap: number; sampleMessage: string };
}

export interface NoticeDeliveryReport {
  noticeId: string;
  title: string;
  publishedAt: string | null;
  deliveries: Array<{
    _id: string;
    channel: 'sms' | 'push';
    recipientKey: string;
    childCount: number;
    status: 'claimed' | 'sent' | 'failed' | 'skipped';
    provider: string;
    error: string;
    sentAt: string | null;
  }>;
}

export interface ParentNotice {
  _id: string;
  title: string;
  body: string;
  priority: NoticePriority;
  publishedAt: string;
}

/* ── Field-app types (parent & gate web) ──────────────────────────────────────
   These back the phone-shaped web versions of the two mobile apps. Same
   endpoints the React Native clients call, so nothing new is needed on the API. */

export interface ChildCode {
  studentId: string;
  studentName: string;
  studentClass: string;
  photoUrl?: string;
  code: string;
  isUsed: boolean;
  isRevoked: boolean;
  isAbsent: boolean;
  expiresAt: string;
}

export interface ParentEvent {
  id: string;
  studentName: string;
  eventType: string;
  collectorName?: string;
  gateOfficerName?: string;
  createdAt: string;
}

export interface AuthResult {
  accessToken: string;
  user: { id: string; role: string; fullName?: string; email?: string; institutionId?: string };
}

/** What /codes/check returns — a peek that does NOT consume the code. */
export interface VerifyResult {
  studentId: string;
  studentName: string;
  studentClass: string;
  studentPhotoUrl: string | null;
  collectors: Array<{
    id: string;
    fullName: string;
    relationship: string;
    photoUrl: string | null;
    isPrimary: boolean;
  }>;
  releasedAt: string;
  /* What the register says about this child today. The gate refuses absent and
     — unless the school has turned the requirement off — unmarked, so a result
     that reaches the screen normally reads 'present'. Shown anyway: a
     confirmation screen that does not state it is asking an officer to hand
     over a child on faith. */
  attendanceStatus: AttendanceState;
  attendanceMarkedAt: string | null;
}

/* What six digits resolves to. A LIST because two children in one school can
   hold the same code on the same day — see code-index.ts on the API. */
export interface ResolveMatch {
  studentId: string;
  fullName: string;
  studentClass: string;
  photoUrl: string | null;
  /* Lets the picker show which of two same-code children the register says is
     even in school, before the officer chooses one. */
  attendanceStatus: AttendanceState;
}

export interface DismissalRow {
  _id: string;
  studentId: string;
  studentName: string;
  studentClass: string;
  photoUrl: string | null;
  status: string;
  calledAt: string;
  calledVia: 'gate' | 'parent_app';
  releasedAt: string | null;
  waitingMinutes: number;
  /** True when the child is in one of the reader's own classes. */
  isMine: boolean;
}

export interface DismissalBoard {
  date: string;
  waiting: DismissalRow[];
  released: DismissalRow[];
}

export interface StudentLookup {
  studentId: string;
  fullName: string;
  studentClass: string;
  studentCode: string;
}

export interface GateVerifyArgs {
  code: string;
  studentId: string;
  deviceId: string;
  gateOfficerId: string;
  institutionId: string;
}

// ─── Base query ───────────────────────────────────────────────────────────────

const rawBaseQuery = fetchBaseQuery({
  baseUrl: '/api/v1',
  credentials: 'include',
  prepareHeaders: (headers) => {
    const token = localStorage.getItem('access_token');
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return headers;
  },
});

/*
  Unwraps the success envelope, and deals with an expired token.

  Every screen in the dashboard reads through here, so this is the one place
  that has to notice a 401 — without it a tab left open through a lesson comes
  back looking signed in while every query underneath it fails. See lib/session
  for why the refresh is single-flight and why auth routes are exempt.
*/
const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  const path = typeof args === 'string' ? args : args.url;
  if (result.error?.status === 401 && !isAuthPath(path)) {
    const token = await refreshAccessToken();
    if (token) {
      result = await rawBaseQuery(args, api, extraOptions);
      if (result.error?.status === 401) endSession();
    } else {
      endSession();
    }
  }

  if (result.data) {
    const res = result.data as { success: boolean; data?: unknown };
    if (res.success) return { data: res.data };
  }
  return result;
};

// ─── Helper ───────────────────────────────────────────────────────────────────

/*
  The API answers with an error in one of TWO shapes, so both are handled here.

  1. The documented envelope:  { success: false, error: { code, message } }
  2. Fastify's own flat shape: { statusCode, code, error: 'Bad Request', message }

  Shape 2 is what routes actually return today, because apps/api/src/app.ts calls
  `setErrorHandler` AFTER registering every route — each `await register()`
  encapsulates its children with whatever handler existed at that moment, so the
  custom one never applies. Until that is fixed the envelope only appears from
  root-level handlers such as the 404.

  This used to read `data.error.message` only. In shape 2 `data.error` is the
  string "Bad Request", so the branch was skipped and every code-verification and
  login failure in the product showed "An unexpected error occurred." instead of
  the real reason — the officer never saw "This code has expired."

  Reading both shapes is correct either way, and keeps working once the API is
  fixed.
*/
function envelope(error: unknown): Record<string, unknown> | null {
  if (!error || typeof error !== 'object') return null;
  const e = error as Record<string, unknown>;
  if (!('data' in e) || !e.data || typeof e.data !== 'object') return null;
  return e.data as Record<string, unknown>;
}

export function getApiError(error: unknown): string {
  if (!error || typeof error !== 'object') return 'An unexpected error occurred.';
  const e = error as Record<string, unknown>;

  // RTK Query FetchBaseQueryError — network / protocol failures
  if ('status' in e) {
    if (e.status === 'FETCH_ERROR') return 'Could not reach the server. Check your network connection.';
    if (e.status === 'TIMEOUT_ERROR') return 'The request timed out. Please try again.';
    if (e.status === 'PARSING_ERROR') return 'The server returned an unexpected response.';
  }

  const data = envelope(error);
  if (data) {
    // Shape 1 — the documented envelope.
    if (data.error && typeof data.error === 'object') {
      const inner = data.error as Record<string, unknown>;
      if (typeof inner.message === 'string') return inner.message;
    }
    // Shape 2 — Fastify's flat error.
    if (typeof data.message === 'string') return data.message;
  }

  // SerializedError (thrown from middleware or non-fetch errors)
  if ('message' in e && typeof e.message === 'string') return e.message;

  return 'An unexpected error occurred.';
}

/**
 * The API's machine-readable error code (`CODE_002`, `STUDENT_ABSENT`, …), from
 * either shape. Null when the response carries none.
 *
 * The gate screen branches on this, so it must not silently read `undefined` —
 * that would show "code not recognised" for a child who is simply marked absent.
 */
export function getApiErrorCode(error: unknown): string | null {
  const data = envelope(error);
  if (!data) return null;
  if (data.error && typeof data.error === 'object') {
    const inner = data.error as Record<string, unknown>;
    if (typeof inner.code === 'string') return inner.code;
  }
  if (typeof data.code === 'string') return data.code;
  return null;
}

// ─── API slice ────────────────────────────────────────────────────────────────

export const safepassApi = createApi({
  reducerPath: 'safepassApi',
  baseQuery,
  tagTypes: ['PickupBoard', 'Students', 'Student', 'Collectors', 'Delegates', 'Staff', 'AuditLog', 'Attendance', 'Groups', 'MyChildren', 'MyHistory', 'MyFees', 'Events', 'EventResponses', 'MyEvents', 'Notices', 'MyNotices', 'Dismissal', 'FeeStructures', 'Invoices', 'Invoice', 'FeeSummary', 'Exams', 'Subjects', 'MarkSheet', 'ExamResults', 'MyResults', 'GradingScale', 'Periods', 'Timetable'],
  endpoints: (builder) => ({

    // Pickup board
    getPickupBoard: builder.query<PickupBoard, string>({
      query: (institutionId) => `/institutions/${institutionId}/pickup-board`,
      providesTags: ['PickupBoard'],
    }),

    // Audit log
    getAuditLog: builder.query<PaginatedResult<AuditEvent>, {
      institutionId: string;
      page?: number;
      pageSize?: number;
      studentId?: string;
      eventType?: string;
      from?: string;
      to?: string;
    }>({
      query: ({ institutionId, ...params }) => {
        const qs = new URLSearchParams();
        if (params.page) qs.set('page', String(params.page));
        if (params.pageSize) qs.set('pageSize', String(params.pageSize));
        if (params.studentId) qs.set('studentId', params.studentId);
        if (params.eventType) qs.set('eventType', params.eventType);
        if (params.from) qs.set('from', params.from);
        if (params.to) qs.set('to', params.to);
        return `/institutions/${institutionId}/audit-log?${qs}`;
      },
      providesTags: ['AuditLog'],
    }),

    // Groups
    listGroups: builder.query<Group[], string>({
      query: (institutionId) => `/institutions/${institutionId}/groups`,
      providesTags: (_, __, institutionId) => [{ type: 'Groups', id: institutionId }],
    }),

    createGroup: builder.mutation<Group, { institutionId: string; data: { name: string; description?: string; order?: number } }>({
      query: ({ institutionId, data }) => ({
        url: `/institutions/${institutionId}/groups`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_, __, { institutionId }) => [{ type: 'Groups', id: institutionId }],
    }),

    updateGroup: builder.mutation<Group, { institutionId: string; groupId: string; data: { name?: string; description?: string; order?: number } }>({
      query: ({ institutionId, groupId, data }) => ({
        url: `/institutions/${institutionId}/groups/${groupId}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_, __, { institutionId }) => [{ type: 'Groups', id: institutionId }],
    }),

    deleteGroup: builder.mutation<void, { institutionId: string; groupId: string }>({
      query: ({ institutionId, groupId }) => ({
        url: `/institutions/${institutionId}/groups/${groupId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, { institutionId }) => [{ type: 'Groups', id: institutionId }],
    }),

    // Students
    listStudents: builder.query<PaginatedResult<StudentSummary>, {
      institutionId: string;
      page?: number;
      pageSize?: number;
      groupId?: string;
      status?: string;
      search?: string;
    }>({
      query: ({ institutionId, ...params }) => {
        const qs = new URLSearchParams();
        if (params.page) qs.set('page', String(params.page));
        if (params.pageSize) qs.set('pageSize', String(params.pageSize));
        if (params.groupId) qs.set('groupId', params.groupId);
        if (params.status) qs.set('status', params.status);
        if (params.search) qs.set('search', params.search);
        return `/institutions/${institutionId}/students?${qs}`;
      },
      providesTags: ['Students'],
    }),

    getStudent: builder.query<StudentSummary & { collectors: unknown[]; photoUrl: string | null }, {
      institutionId: string;
      studentId: string;
    }>({
      query: ({ institutionId, studentId }) =>
        `/institutions/${institutionId}/students/${studentId}`,
      providesTags: (_, __, { studentId }) => [{ type: 'Student', id: studentId }],
    }),

    createStudent: builder.mutation<StudentSummary, { institutionId: string; data: CreateStudentData }>({
      query: ({ institutionId, data }) => ({
        url: `/institutions/${institutionId}/students`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Students'],
    }),

    uploadStudentPhoto: builder.mutation<{ photoUrl: string }, { institutionId: string; studentId: string; file: File }>({
      query: ({ institutionId, studentId, file }) => {
        const body = new FormData();
        body.append('file', file);
        return {
          url: `/institutions/${institutionId}/students/${studentId}/photo`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: (_, __, { studentId }) => [{ type: 'Student', id: studentId }],
    }),

    updateStudent: builder.mutation<StudentSummary, {
      institutionId: string;
      studentId: string;
      data: Partial<{ fullName: string; dateOfBirth: string; groupId: string | null; primaryCollectorPhone: string; status: string }>;
    }>({
      query: ({ institutionId, studentId, data }) => ({
        url: `/institutions/${institutionId}/students/${studentId}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_, __, { studentId }) => ['Students', { type: 'Student', id: studentId }],
    }),

    bulkImportStudents: builder.mutation<
      { created: number; errors: Array<{ row: number; message: string }> },
      { institutionId: string; rows: Array<{ fullName: string; dateOfBirth: string; groupName: string; primaryCollectorPhone: string }> }
    >({
      query: ({ institutionId, rows }) => ({
        url: `/institutions/${institutionId}/students/bulk-import`,
        method: 'POST',
        body: { rows },
      }),
      invalidatesTags: ['Students'],
    }),

    // Siblings
    getSiblings: builder.query<{ _id: string; fullName: string; class: string }[], { institutionId: string; studentId: string }>({
      query: ({ institutionId, studentId }) => `/institutions/${institutionId}/students/${studentId}/siblings`,
    }),

    // Collectors
    listCollectors: builder.query<CollectorSummary[], string>({
      query: (studentId) => `/students/${studentId}/collectors`,
      providesTags: (_, __, studentId) => [{ type: 'Collectors', id: studentId }],
    }),

    createCollector: builder.mutation<CollectorSummary, {
      studentId: string;
      data: Omit<CollectorSummary, '_id' | 'isActive' | 'additionalPhones'> & { additionalPhones?: string[] };
    }>({
      query: ({ studentId, data }) => ({
        url: `/students/${studentId}/collectors`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_, __, { studentId }) => [{ type: 'Collectors', id: studentId }],
    }),

    updateCollector: builder.mutation<CollectorSummary, { studentId: string; collectorId: string; data: Partial<CollectorSummary> }>({
      query: ({ studentId, collectorId, data }) => ({
        url: `/students/${studentId}/collectors/${collectorId}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_, __, { studentId }) => [{ type: 'Collectors', id: studentId }],
    }),

    copyCollectorsFrom: builder.mutation<CollectorSummary[], { studentId: string; sourceStudentId: string }>({
      query: ({ studentId, sourceStudentId }) => ({
        url: `/students/${studentId}/collectors/copy-from/${sourceStudentId}`,
        method: 'POST',
      }),
      invalidatesTags: (_, __, { studentId }) => [{ type: 'Collectors', id: studentId }],
    }),

    deactivateCollector: builder.mutation<void, { studentId: string; collectorId: string }>({
      query: ({ studentId, collectorId }) => ({
        url: `/students/${studentId}/collectors/${collectorId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, { studentId }) => [{ type: 'Collectors', id: studentId }],
    }),

    // Delegates
    listDelegates: builder.query<DelegateSummary[], string>({
      query: (studentId) => `/students/${studentId}/delegates`,
      providesTags: (_, __, studentId) => [{ type: 'Delegates', id: studentId }],
    }),

    addDelegate: builder.mutation<DelegateSummary, { studentId: string; data: AddDelegateData }>({
      query: ({ studentId, data }) => ({
        url: `/students/${studentId}/delegates`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_, __, { studentId }) => [{ type: 'Delegates', id: studentId }],
    }),

    cancelDelegate: builder.mutation<void, { studentId: string; delegateId: string }>({
      query: ({ studentId, delegateId }) => ({
        url: `/students/${studentId}/delegates/${delegateId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, { studentId }) => [{ type: 'Delegates', id: studentId }],
    }),

    // Staff
    listStaff: builder.query<StaffMember[], string>({
      query: (institutionId) => `/institutions/${institutionId}/staff`,
      providesTags: ['Staff'],
    }),

    updateStaff: builder.mutation<
      null,
      {
        institutionId: string;
        userId: string;
        data: Partial<CreateStaffData> & { isActive?: boolean };
      }
    >({
      query: ({ institutionId, userId, data }) => ({
        url: `/institutions/${institutionId}/staff/${userId}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Staff'],
    }),

    uploadStaffImage: builder.mutation<
      { url: string },
      { institutionId: string; userId: string; kind: 'photo' | 'signature'; file: File }
    >({
      query: ({ institutionId, userId, kind, file }) => {
        const body = new FormData();
        body.append('file', file);
        return { url: `/institutions/${institutionId}/staff/${userId}/image/${kind}`, method: 'POST', body };
      },
      invalidatesTags: ['Staff'],
    }),

    removeStaffImage: builder.mutation<
      null,
      { institutionId: string; userId: string; kind: 'photo' | 'signature' }
    >({
      query: ({ institutionId, userId, kind }) => ({
        url: `/institutions/${institutionId}/staff/${userId}/image/${kind}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Staff'],
    }),

    createStaff: builder.mutation<StaffMember, { institutionId: string; data: CreateStaffData }>({
      query: ({ institutionId, data }) => ({
        url: `/institutions/${institutionId}/staff`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Staff'],
    }),

    deactivateStaff: builder.mutation<null, { institutionId: string; userId: string }>({
      query: ({ institutionId, userId }) => ({
        url: `/institutions/${institutionId}/staff/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Staff'],
    }),

    /* ── Attendance ──────────────────────────────────────────────────────────
       The register returns EVERY active child, including the ones nobody has
       decided about yet (`unmarked`), because that third state is the whole
       point: a screen that lists only present and absent cannot show you that
       Class 3's register was never taken. */

    /*
      Who went home, and — the part no event log can answer — who did not.

      A child not being collected is the ABSENCE of an event, so this starts
      from the roll rather than from the audit log.
    */
    getDismissalRegister: builder.query<DismissalRegister, { institutionId: string; date: string }>({
      query: ({ institutionId, date }) => `/institutions/${institutionId}/reports/dismissal?date=${date}`,
      providesTags: ['PickupBoard'],
    }),

    /* The school's own name, for the top of anything printed. Already served
       for report cards; a timetable pinned on a wall wants the same header. */
    getInstitutionIdentity: builder.query<
      { _id: string; name: string; address: string; phone: string; email: string; logoUrl: string | null },
      string
    >({
      query: (institutionId) => `/institutions/${institutionId}`,
    }),

    listPeriods: builder.query<Period[], string>({
      query: (institutionId) => `/institutions/${institutionId}/periods`,
      providesTags: ['Periods'],
    }),

    setPeriods: builder.mutation<
      Period[],
      { institutionId: string; periods: Array<Omit<Period, '_id' | 'order'> & { _id?: string }> }
    >({
      query: ({ institutionId, periods }) => ({
        url: `/institutions/${institutionId}/periods`,
        method: 'PUT',
        body: { periods },
      }),
      invalidatesTags: ['Periods', 'Timetable'],
    }),

    getClassTimetable: builder.query<ClassTimetable, { institutionId: string; groupId: string }>({
      query: ({ institutionId, groupId }) => `/institutions/${institutionId}/timetable?groupId=${groupId}`,
      providesTags: ['Timetable'],
    }),

    /* "Where am I now" — reads the caller's own id, so it cannot be pointed
       at a colleague. */
    getMyTimetable: builder.query<TeacherTimetable, string>({
      query: (institutionId) => `/institutions/${institutionId}/timetable/mine`,
      providesTags: ['Timetable'],
    }),

    setSlot: builder.mutation<
      null,
      {
        institutionId: string;
        groupId: string;
        day: number;
        periodId: string;
        subjectId: string;
        teacherId?: string | null;
        room?: string;
      }
    >({
      query: ({ institutionId, ...body }) => ({
        url: `/institutions/${institutionId}/timetable/slot`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Timetable'],
    }),

    clearSlot: builder.mutation<null, { institutionId: string; groupId: string; day: number; periodId: string }>({
      query: ({ institutionId, ...body }) => ({
        url: `/institutions/${institutionId}/timetable/slot/clear`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Timetable'],
    }),

    /*
      Copying, which is the difference between a timetable module that gets
      used and one that gets abandoned: 35 slots a week × ten classes is the
      better part of an hour of clicking otherwise.

      Both REPLACE the target, so the screen asks first.
    */
    copyTimetableDay: builder.mutation<
      { copied: number; teachersDropped: number },
      { institutionId: string; groupId: string; fromDay: number; toDays: number[] }
    >({
      query: ({ institutionId, ...body }) => ({
        url: `/institutions/${institutionId}/timetable/copy-day`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Timetable'],
    }),

    copyTimetableClass: builder.mutation<
      { copied: number; teachersDropped: number },
      { institutionId: string; fromGroupId: string; toGroupId: string }
    >({
      query: ({ institutionId, ...body }) => ({
        url: `/institutions/${institutionId}/timetable/copy-class`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Timetable'],
    }),

    /* Double-booking is one failure; one teacher on 35 periods while another
       is on 4 is the other, and no clash check can see it. */
    getTeacherLoad: builder.query<TeacherLoadRow[], string>({
      query: (institutionId) => `/institutions/${institutionId}/timetable/teacher-load`,
      providesTags: ['Timetable'],
    }),

    getMyChildrenTimetable: builder.query<ChildTimetable[], void>({
      query: () => '/parent/children/timetable',
      providesTags: ['Timetable'],
    }),

    getRegister: builder.query<AttendanceRegister, { institutionId: string; date: string }>({
      query: ({ institutionId, date }) => `/institutions/${institutionId}/attendance?date=${date}`,
      providesTags: (_, __, { institutionId, date }) => [{ type: 'Attendance', id: `${institutionId}-${date}` }],
    }),

    /* One mutation for one child or forty — the API takes a list either way. */
    markAttendance: builder.mutation<
      { marked: number },
      { institutionId: string; date: string; studentIds: string[]; status: 'present' | 'absent'; note?: string }
    >({
      query: ({ institutionId, ...body }) => ({
        url: `/institutions/${institutionId}/attendance/mark`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, __, { institutionId, date }) => [{ type: 'Attendance', id: `${institutionId}-${date}` }],
    }),

    clearAttendance: builder.mutation<
      { cleared: number },
      { institutionId: string; date: string; studentIds: string[] }
    >({
      query: ({ institutionId, ...body }) => ({
        url: `/institutions/${institutionId}/attendance/clear`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, __, { institutionId, date }) => [{ type: 'Attendance', id: `${institutionId}-${date}` }],
    }),


    // ── Exam results ────────────────────────────────────────────────────────

    /* The school's own name and address, for the top of a printed report card. */
    getInstitution: builder.query<
      { _id: string; name: string; address: string; phone: string; email: string; logoUrl: string | null },
      string
    >({
      query: (institutionId) => `/institutions/${institutionId}`,
    }),

    /* `all` includes retired subjects, so one retired by mistake can be
       restored rather than quietly becoming unreachable. */
    listSubjects: builder.query<Subject[], { institutionId: string; all?: boolean }>({
      query: ({ institutionId, all }) => `/institutions/${institutionId}/subjects${all ? '?all=true' : ''}`,
      providesTags: ['Subjects'],
    }),

    createSubject: builder.mutation<Subject, { institutionId: string; name: string; code: string; groupIds?: string[] }>({
      query: ({ institutionId, ...body }) => ({ url: `/institutions/${institutionId}/subjects`, method: 'POST', body }),
      invalidatesTags: ['Subjects'],
    }),

    deleteSubject: builder.mutation<null, { institutionId: string; subjectId: string }>({
      query: ({ institutionId, subjectId }) => ({
        url: `/institutions/${institutionId}/subjects/${subjectId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Subjects'],
    }),

    deleteExam: builder.mutation<null, { institutionId: string; examId: string }>({
      query: ({ institutionId, examId }) => ({
        url: `/institutions/${institutionId}/exams/${examId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Exams', 'Subjects', 'MarkSheet', 'ExamResults'],
    }),

    updateSubject: builder.mutation<Subject, { institutionId: string; subjectId: string; isActive?: boolean; name?: string; code?: string }>({
      query: ({ institutionId, subjectId, ...body }) => ({
        url: `/institutions/${institutionId}/subjects/${subjectId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Subjects'],
    }),

    /* The classes the caller may open — every class for the office, their own
       for a teacher, in one request so no screen has to guess. */
    /* The classes with their roll and their staff — the view somebody opens
       to set a year up. */
    listGroupsWithStaff: builder.query<
      Array<{
        _id: string;
        name: string;
        description: string;
        order: number;
        isActive: boolean;
        studentCount: number;
        staff: Array<{ id: string; fullName: string; role: string }>;
      }>,
      string
    >({
      query: (institutionId) => `/institutions/${institutionId}/groups-with-staff`,
      providesTags: ['Groups'],
    }),

    setGroupStaff: builder.mutation<null, { institutionId: string; groupId: string; userIds: string[] }>({
      query: ({ institutionId, groupId, userIds }) => ({
        url: `/institutions/${institutionId}/groups/${groupId}/staff`,
        method: 'PUT',
        body: { userIds },
      }),
      /* Staff too: the same link drives the Staff page's class chips. */
      invalidatesTags: ['Groups', 'Staff'],
    }),

    listExamGroups: builder.query<Array<{ _id: string; name: string }>, string>({
      query: (institutionId) => `/institutions/${institutionId}/exam-groups`,
      providesTags: ['Groups'],
    }),

    listExams: builder.query<ExamSummary[], string>({
      query: (institutionId) => `/institutions/${institutionId}/exams`,
      providesTags: ['Exams'],
    }),

    createExam: builder.mutation<ExamSummary, { institutionId: string } & Record<string, unknown>>({
      query: ({ institutionId, ...body }) => ({ url: `/institutions/${institutionId}/exams`, method: 'POST', body }),
      invalidatesTags: ['Exams'],
    }),

    updateExam: builder.mutation<ExamSummary, { institutionId: string; examId: string } & Record<string, unknown>>({
      query: ({ institutionId, examId, ...body }) => ({
        url: `/institutions/${institutionId}/exams/${examId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Exams', 'MarkSheet', 'ExamResults'],
    }),

    publishExam: builder.mutation<ExamSummary, { institutionId: string; examId: string }>({
      query: ({ institutionId, examId }) => ({
        url: `/institutions/${institutionId}/exams/${examId}/publish`,
        method: 'POST',
      }),
      invalidatesTags: ['Exams', 'MarkSheet'],
    }),

    unpublishExam: builder.mutation<ExamSummary, { institutionId: string; examId: string }>({
      query: ({ institutionId, examId }) => ({
        url: `/institutions/${institutionId}/exams/${examId}/unpublish`,
        method: 'POST',
      }),
      invalidatesTags: ['Exams', 'MarkSheet'],
    }),

    getMarkSheet: builder.query<MarkSheet, { institutionId: string; examId: string; groupId: string; subjectId: string }>({
      query: ({ institutionId, examId, groupId, subjectId }) =>
        `/institutions/${institutionId}/exams/${examId}/sheet?groupId=${groupId}&subjectId=${subjectId}`,
      providesTags: ['MarkSheet'],
    }),

    saveScores: builder.mutation<
      { saved: number },
      { institutionId: string; examId: string; subjectId: string; scores: Array<{ studentId: string; classScore: number; examScore: number }> }
    >({
      query: ({ institutionId, examId, ...body }) => ({
        url: `/institutions/${institutionId}/exams/${examId}/scores`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['MarkSheet', 'Exams', 'ExamResults'],
    }),

    /* Back to unmarked. A mark typed against the wrong child could be
       overwritten but never removed, which left the only fix as inventing a
       number for a paper nobody sat. */
    clearScore: builder.mutation<
      null,
      { institutionId: string; examId: string; subjectId: string; studentId: string }
    >({
      query: ({ institutionId, examId, subjectId, studentId }) => ({
        url: `/institutions/${institutionId}/exams/${examId}/scores/${subjectId}/${studentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['MarkSheet', 'Exams', 'ExamResults'],
    }),

    getGradingScale: builder.query<GradeBand[], string>({
      query: (institutionId) => `/institutions/${institutionId}/grading-scale`,
      providesTags: ['GradingScale'],
    }),

    setGradingScale: builder.mutation<GradeBand[], { institutionId: string; bands: GradeBand[] }>({
      query: ({ institutionId, bands }) => ({
        url: `/institutions/${institutionId}/grading-scale`,
        method: 'PUT',
        body: { bands },
      }),
      invalidatesTags: ['GradingScale'],
    }),

    saveRemark: builder.mutation<
      null,
      { institutionId: string; examId: string; studentId: string; classTeacherRemark?: string; headTeacherRemark?: string }
    >({
      query: ({ institutionId, examId, ...body }) => ({
        url: `/institutions/${institutionId}/exams/${examId}/remarks`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ExamResults', 'MyResults'],
    }),

    getClassResults: builder.query<ReportCard[], { institutionId: string; examId: string; groupId: string }>({
      query: ({ institutionId, examId, groupId }) =>
        `/institutions/${institutionId}/exams/${examId}/results?groupId=${groupId}`,
      providesTags: ['ExamResults'],
    }),

    /* Parent-facing. Published exams only — the filter lives in the API. */
    getMyResults: builder.query<ChildResults[], void>({
      query: () => '/parent/children/results',
      providesTags: ['MyResults'],
    }),

    /* ── Parent app (web) ────────────────────────────────────────────────────
       Phone OTP, then the child's code for today. */

    requestOtp: builder.mutation<void, string>({
      query: (phone) => ({ url: '/auth/otp/request', method: 'POST', body: { phone } }),
    }),

    verifyOtp: builder.mutation<AuthResult, { phone: string; otp: string }>({
      query: (body) => ({ url: '/auth/otp/verify', method: 'POST', body }),
    }),

    getMyChildren: builder.query<ChildCode[], void>({
      query: () => '/parent/children/codes',
      providesTags: ['MyChildren'],
    }),

    getMyFees: builder.query<ParentFees, void>({
      query: () => '/parent/children/fees',
      providesTags: ['MyFees'],
    }),

    getMyInvoice: builder.query<ParentInvoiceDetail, string>({
      query: (invoiceId) => `/parent/fees/invoices/${invoiceId}`,
      providesTags: (_, __, invoiceId) => [{ type: 'MyFees', id: invoiceId }],
    }),

    getMyHistory: builder.query<ParentEvent[], void>({
      query: () => '/parent/children/history',
      providesTags: ['MyHistory'],
    }),

    revokeMyCode: builder.mutation<ChildCode, string>({
      query: (studentId) => ({ url: '/codes/revoke', method: 'POST', body: { studentId } }),
      invalidatesTags: ['MyChildren', 'MyHistory'],
    }),

    addEmergencyDelegate: builder.mutation<
      DelegateSummary,
      { studentId: string; name: string; phone: string }
    >({
      query: ({ studentId, ...body }) => ({
        url: `/students/${studentId}/delegates/emergency`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Delegates', 'MyHistory'],
    }),

    /* ── Gate app (web) ──────────────────────────────────────────────────────
       lookupStudent resolves the school's own student code to an id; checkCode
       peeks WITHOUT consuming; confirmRelease is the one that spends it. */

    lookupStudentByCode: builder.query<StudentLookup, { institutionId: string; studentCode: string }>({
      query: ({ institutionId, studentCode }) =>
        `/institutions/${institutionId}/students/by-code/${encodeURIComponent(studentCode)}`,
    }),

    getDismissalBoard: builder.query<DismissalBoard, string>({
      query: (institutionId) => `/institutions/${institutionId}/dismissal`,
      providesTags: ['Dismissal'],
    }),

    callToGate: builder.mutation<unknown, { institutionId: string; studentId: string }>({
      query: ({ institutionId, studentId }) => ({
        url: `/institutions/${institutionId}/dismissal/call`,
        method: 'POST',
        body: { studentId },
      }),
      invalidatesTags: ['Dismissal'],
    }),

    cancelCall: builder.mutation<unknown, { institutionId: string; entryId: string; reason?: string }>({
      query: ({ institutionId, entryId, reason }) => ({
        url: `/institutions/${institutionId}/dismissal/${entryId}/cancel`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['Dismissal'],
    }),

    announceArrival: builder.mutation<{ status: string; calledAt: string }, { studentId: string }>({
      query: (body) => ({ url: '/parent/dismissal/announce', method: 'POST', body }),
      invalidatesTags: ['MyChildren'],
    }),

    resolveCode: builder.mutation<{ matches: ResolveMatch[] }, { code: string; deviceId: string }>({
      query: (body) => ({ url: '/codes/resolve', method: 'POST', body }),
    }),

    checkCode: builder.mutation<VerifyResult, GateVerifyArgs>({
      query: (body) => ({ url: '/codes/check', method: 'POST', body }),
    }),

    confirmRelease: builder.mutation<VerifyResult, GateVerifyArgs>({
      query: (body) => ({ url: '/codes/verify', method: 'POST', body }),
      invalidatesTags: ['PickupBoard', 'AuditLog'],
    }),

    // ── Fees ────────────────────────────────────────────────────────────────

    listFeeStructures: builder.query<FeeStructure[], string>({
      query: (institutionId) => `/institutions/${institutionId}/fees/structures`,
      providesTags: ['FeeStructures'],
    }),

    createFeeStructure: builder.mutation<FeeStructure, {
      institutionId: string;
      data: {
        name: string;
        academicYear: string;
        term: Term;
        groupId: string | null;
        items: FeeItem[];
        dueDate: string;
      };
    }>({
      query: ({ institutionId, data }) => ({
        url: `/institutions/${institutionId}/fees/structures`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['FeeStructures'],
    }),

    updateFeeStructure: builder.mutation<FeeStructure, {
      institutionId: string;
      structureId: string;
      data: Partial<{ name: string; academicYear: string; term: Term; groupId: string | null; items: FeeItem[]; dueDate: string }>;
    }>({
      query: ({ institutionId, structureId, data }) => ({
        url: `/institutions/${institutionId}/fees/structures/${structureId}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['FeeStructures'],
    }),

    archiveFeeStructure: builder.mutation<null, { institutionId: string; structureId: string }>({
      query: ({ institutionId, structureId }) => ({
        url: `/institutions/${institutionId}/fees/structures/${structureId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FeeStructures'],
    }),

    /* Raising bills a whole class, so it invalidates the list and the figures
       above it — not just the structure it was run from. */
    raiseInvoices: builder.mutation<RaiseResult, { institutionId: string; structureId: string }>({
      query: ({ institutionId, structureId }) => ({
        url: `/institutions/${institutionId}/fees/structures/${structureId}/raise`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: ['Invoices', 'FeeSummary', 'FeeStructures'],
    }),

    listInvoices: builder.query<PaginatedResult<InvoiceRow>, {
      institutionId: string;
      page?: number;
      pageSize?: number;
      academicYear?: string;
      term?: Term;
      status?: InvoiceStatus | 'outstanding' | 'overdue';
      groupId?: string;
      search?: string;
    }>({
      query: ({ institutionId, ...params }) => {
        const qs = new URLSearchParams();
        if (params.page) qs.set('page', String(params.page));
        if (params.pageSize) qs.set('pageSize', String(params.pageSize));
        if (params.academicYear) qs.set('academicYear', params.academicYear);
        if (params.term) qs.set('term', params.term);
        if (params.status) qs.set('status', params.status);
        if (params.groupId) qs.set('groupId', params.groupId);
        if (params.search) qs.set('search', params.search);
        return `/institutions/${institutionId}/fees/invoices?${qs}`;
      },
      providesTags: ['Invoices'],
    }),

    getInvoice: builder.query<InvoiceDetail, { institutionId: string; invoiceId: string }>({
      query: ({ institutionId, invoiceId }) =>
        `/institutions/${institutionId}/fees/invoices/${invoiceId}`,
      providesTags: (_, __, { invoiceId }) => [{ type: 'Invoice', id: invoiceId }],
    }),

    recordPayment: builder.mutation<{ receiptNumber: string }, {
      institutionId: string;
      invoiceId: string;
      data: { amountMinor: number; method: PaymentMethod; reference?: string; note?: string };
    }>({
      query: ({ institutionId, invoiceId, data }) => ({
        url: `/institutions/${institutionId}/fees/invoices/${invoiceId}/payments`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_, __, { invoiceId }) => [
        { type: 'Invoice', id: invoiceId },
        'Invoices',
        'FeeSummary',
      ],
    }),

    getFeeSummary: builder.query<FeeSummary, { institutionId: string; academicYear?: string; term?: Term }>({
      query: ({ institutionId, academicYear, term }) => {
        const qs = new URLSearchParams();
        if (academicYear) qs.set('academicYear', academicYear);
        if (term) qs.set('term', term);
        return `/institutions/${institutionId}/fees/summary?${qs}`;
      },
      providesTags: ['FeeSummary'],
    }),

    // ── Events ──────────────────────────────────────────────────────────────

    listEvents: builder.query<SchoolEvent[], { institutionId: string; status?: EventStatus; category?: EventCategory; from?: string; to?: string }>({
      query: ({ institutionId, ...p }) => {
        const qs = new URLSearchParams();
        if (p.status) qs.set('status', p.status);
        if (p.category) qs.set('category', p.category);
        if (p.from) qs.set('from', p.from);
        if (p.to) qs.set('to', p.to);
        return `/institutions/${institutionId}/events?${qs}`;
      },
      providesTags: ['Events'],
    }),

    createEvent: builder.mutation<SchoolEvent, { institutionId: string; data: EventInput }>({
      query: ({ institutionId, data }) => ({ url: `/institutions/${institutionId}/events`, method: 'POST', body: data }),
      invalidatesTags: ['Events'],
    }),

    updateEvent: builder.mutation<SchoolEvent, { institutionId: string; eventId: string; data: Partial<EventInput> }>({
      query: ({ institutionId, eventId, data }) => ({ url: `/institutions/${institutionId}/events/${eventId}`, method: 'PATCH', body: data }),
      invalidatesTags: ['Events'],
    }),

    publishEvent: builder.mutation<SchoolEvent, { institutionId: string; eventId: string }>({
      query: ({ institutionId, eventId }) => ({ url: `/institutions/${institutionId}/events/${eventId}/publish`, method: 'POST', body: {} }),
      invalidatesTags: ['Events'],
    }),

    cancelEvent: builder.mutation<SchoolEvent, { institutionId: string; eventId: string; reason: string }>({
      query: ({ institutionId, eventId, reason }) => ({ url: `/institutions/${institutionId}/events/${eventId}/cancel`, method: 'POST', body: { reason } }),
      invalidatesTags: ['Events'],
    }),

    getReminderPreview: builder.query<ReminderPreview, { institutionId: string; eventId: string }>({
      query: ({ institutionId, eventId }) => `/institutions/${institutionId}/events/${eventId}/reminders`,
      providesTags: (_, __, { eventId }) => [{ type: 'Events', id: `reminders-${eventId}` }],
    }),

    getEventResponses: builder.query<EventResponses, { institutionId: string; eventId: string }>({
      query: ({ institutionId, eventId }) => `/institutions/${institutionId}/events/${eventId}/responses`,
      providesTags: (_, __, { eventId }) => [{ type: 'EventResponses', id: eventId }],
    }),

    // Parent-facing
    getMyEvents: builder.query<ParentCalendarEvent[], void>({
      query: () => '/parent/events',
      providesTags: ['MyEvents'],
    }),

    respondToEvent: builder.mutation<ParentCalendarEvent[], { eventId: string; studentId: string; response: RsvpAnswer; guestCount?: number; note?: string }>({
      query: ({ eventId, ...body }) => ({ url: `/parent/events/${eventId}/responses`, method: 'POST', body }),
      invalidatesTags: ['MyEvents'],
    }),

    // ── Notices ─────────────────────────────────────────────────────────────

    listNotices: builder.query<Notice[], { institutionId: string; status?: NoticeStatus; priority?: NoticePriority }>({
      query: ({ institutionId, ...p }) => {
        const qs = new URLSearchParams();
        if (p.status) qs.set('status', p.status);
        if (p.priority) qs.set('priority', p.priority);
        return `/institutions/${institutionId}/notices?${qs}`;
      },
      providesTags: ['Notices'],
    }),

    createNotice: builder.mutation<Notice, { institutionId: string; data: NoticeInput }>({
      query: ({ institutionId, data }) => ({ url: `/institutions/${institutionId}/notices`, method: 'POST', body: data }),
      invalidatesTags: ['Notices'],
    }),

    updateNotice: builder.mutation<Notice, { institutionId: string; noticeId: string; data: Partial<NoticeInput> }>({
      query: ({ institutionId, noticeId, data }) => ({ url: `/institutions/${institutionId}/notices/${noticeId}`, method: 'PATCH', body: data }),
      invalidatesTags: ['Notices'],
    }),

    publishNotice: builder.mutation<{ notice: Notice; result: { pushSent: number; smsSent: number; smsSuppressed: boolean } }, { institutionId: string; noticeId: string }>({
      query: ({ institutionId, noticeId }) => ({ url: `/institutions/${institutionId}/notices/${noticeId}/publish`, method: 'POST', body: {} }),
      invalidatesTags: ['Notices', 'MyNotices'],
    }),

    cancelNotice: builder.mutation<Notice, { institutionId: string; noticeId: string }>({
      query: ({ institutionId, noticeId }) => ({ url: `/institutions/${institutionId}/notices/${noticeId}/cancel`, method: 'POST', body: {} }),
      invalidatesTags: ['Notices', 'MyNotices'],
    }),

    getNoticePreview: builder.query<NoticePreview, { institutionId: string; noticeId: string }>({
      query: ({ institutionId, noticeId }) => `/institutions/${institutionId}/notices/${noticeId}/preview`,
      providesTags: (_, __, { noticeId }) => [{ type: 'Notices', id: `preview-${noticeId}` }],
    }),

    getNoticeDelivery: builder.query<NoticeDeliveryReport, { institutionId: string; noticeId: string }>({
      query: ({ institutionId, noticeId }) => `/institutions/${institutionId}/notices/${noticeId}/delivery`,
      providesTags: (_, __, { noticeId }) => [{ type: 'Notices', id: `delivery-${noticeId}` }],
    }),

    getMyNotices: builder.query<ParentNotice[], void>({
      query: () => '/parent/notices',
      providesTags: ['MyNotices'],
    }),
  }),
});

export const {
  useGetPickupBoardQuery,
  useGetAuditLogQuery,
  useListGroupsQuery,
  useCreateGroupMutation,
  useUpdateGroupMutation,
  useDeleteGroupMutation,
  useListStudentsQuery,
  useGetStudentQuery,
  useCreateStudentMutation,
  useUploadStudentPhotoMutation,
  useUpdateStudentMutation,
  useBulkImportStudentsMutation,
  useGetSiblingsQuery,
  useListCollectorsQuery,
  useCreateCollectorMutation,
  useUpdateCollectorMutation,
  useCopyCollectorsFromMutation,
  useDeactivateCollectorMutation,
  useListDelegatesQuery,
  useAddDelegateMutation,
  useCancelDelegateMutation,
  useListStaffQuery,
  useCreateStaffMutation,
  useUpdateStaffMutation,
  useUploadStaffImageMutation,
  useRemoveStaffImageMutation,
  useDeactivateStaffMutation,
  useGetInstitutionQuery,
  useListSubjectsQuery,
  useCreateSubjectMutation,
  useUpdateSubjectMutation,
  useDeleteSubjectMutation,
  useDeleteExamMutation,
  useListGroupsWithStaffQuery,
  useSetGroupStaffMutation,
  useListExamGroupsQuery,
  useListExamsQuery,
  useCreateExamMutation,
  useUpdateExamMutation,
  usePublishExamMutation,
  useUnpublishExamMutation,
  useGetMarkSheetQuery,
  useSaveScoresMutation,
  useClearScoreMutation,
  useGetGradingScaleQuery,
  useSetGradingScaleMutation,
  useSaveRemarkMutation,
  useGetClassResultsQuery,
  useGetMyResultsQuery,
  useGetDismissalRegisterQuery,
  useGetInstitutionIdentityQuery,
  useListPeriodsQuery,
  useSetPeriodsMutation,
  useGetClassTimetableQuery,
  useGetMyTimetableQuery,
  useSetSlotMutation,
  useClearSlotMutation,
  useCopyTimetableDayMutation,
  useCopyTimetableClassMutation,
  useGetTeacherLoadQuery,
  useGetMyChildrenTimetableQuery,
  useGetRegisterQuery,
  useMarkAttendanceMutation,
  useClearAttendanceMutation,
  // Parent app (web)
  useRequestOtpMutation,
  useVerifyOtpMutation,
  useGetMyChildrenQuery,
  useGetMyHistoryQuery,
  useGetMyFeesQuery,
  useGetMyInvoiceQuery,
  useRevokeMyCodeMutation,
  useAddEmergencyDelegateMutation,
  // Gate app (web)
  useLazyLookupStudentByCodeQuery,
  useGetDismissalBoardQuery,
  useCallToGateMutation,
  useCancelCallMutation,
  useAnnounceArrivalMutation,
  useResolveCodeMutation,
  useCheckCodeMutation,
  useConfirmReleaseMutation,
  // Fees
  useListFeeStructuresQuery,
  useCreateFeeStructureMutation,
  useUpdateFeeStructureMutation,
  useArchiveFeeStructureMutation,
  useRaiseInvoicesMutation,
  useListInvoicesQuery,
  useGetInvoiceQuery,
  useRecordPaymentMutation,
  useGetFeeSummaryQuery,
  // Events
  useListEventsQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  usePublishEventMutation,
  useCancelEventMutation,
  useGetEventResponsesQuery,
  useGetReminderPreviewQuery,
  useGetMyEventsQuery,
  useRespondToEventMutation,
  // Notices
  useListNoticesQuery,
  useCreateNoticeMutation,
  useUpdateNoticeMutation,
  usePublishNoticeMutation,
  useCancelNoticeMutation,
  useGetNoticePreviewQuery,
  useGetNoticeDeliveryQuery,
  useGetMyNoticesQuery,
} = safepassApi;
